import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useIntl } from "react-intl-compat";

import {
  UpdateListingPayload,
  useUpdateListingMutation,
} from "../../../../components/src/services/listings.service";
import type { Listing } from "../../../../types/listings.types";

import {
  buildListingRulesFormSchema,
  ListingRulesFormValues,
} from "./ListingRulesForm/ListingRulesForm.schema";

/**
 * Builds the form for the rules page (house rules, minimum age, minimum
 * hours, max guests) and wires up its save action.
 */
const useListingRulesForm = (listing?: Listing) => {
  const intl = useIntl();
  const [updateListing] = useUpdateListingMutation();
  const [errorMsg, setErrorMsg] = React.useState<string>();

  const form = useForm<ListingRulesFormValues>({
    resolver: zodResolver(buildListingRulesFormSchema({ intl })),
    values: listing
      ? {
          rules: listing.rules ?? "",
          minAge: String(listing.min_age ?? 0),
          minimumDuration: Number(listing.minimum_duration ?? 0),
          numberOfGuests: listing.number_guests ?? 0,
        }
      : undefined,
  });

  const onSave = async (): Promise<boolean> => {
    setErrorMsg(undefined);
    let success = false;

    await form.handleSubmit(async (values) => {
      if (!listing) {
        return;
      }

      const payload: UpdateListingPayload = {
        rules: values.rules,
        min_age: Number(values.minAge),
        minimum_duration: String(values.minimumDuration),
        number_guests: values.numberOfGuests,
      };

      try {
        await updateListing({ listingId: listing._id, payload }).unwrap();
        success = true;
      } catch {
        setErrorMsg(
          intl.formatMessage({
            id: "hosting.listing_rules.submit_error",
            defaultMessage: "Something went wrong. Please try again.",
          }),
        );
      }
    })();

    return success;
  };

  return { form, onSave, errorMsg };
};

export { useListingRulesForm };
